import { CREDIT_PRICE, CreditTxTypeEnum, CreditTxWithMeta } from "@garther/interfaces";
import { Button, Countdown } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Check, Copy } from "lucide-react";
import QRCodeStyling from "qr-code-styling";
import { useEffect, useRef, useState } from "react";

interface QRCodeDisplayProps {
  qrCodeData: string;
  checkoutUrl: string;
  onClose: () => void;
  creditTx?: CreditTxWithMeta;
}

export const PaymentQRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  qrCodeData,
  checkoutUrl,
  onClose,
  creditTx
}) => {
  const { t, formatCurrency, formatCredit } = useI18n();
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (qrRef.current && qrCodeData) {
      // Create QR code with styling
      qrCodeRef.current = new QRCodeStyling({
        width: 260,
        height: 260,
        data: qrCodeData,
        dotsOptions: {
          color: "#000000",
          type: "classy-rounded"
        },
        backgroundOptions: {
          color: "transparent"
        },
        imageOptions: {
          crossOrigin: "anonymous",
          margin: 2,
          imageSize: 0.25,
          hideBackgroundDots: true
        },
        image: "/logo.png", // Logo in the center
        cornersSquareOptions: {
          type: "classy-rounded"
        },
        cornersDotOptions: {
          type: "classy-rounded"
        }
      });

      // Append QR code to the container
      qrRef.current.innerHTML = "";
      qrCodeRef.current.append(qrRef.current);

      // Ensure the SVG is properly sized
      const svg = qrRef.current.querySelector("svg");
      if (svg) {
        svg.style.width = "192px";
        svg.style.height = "192px";
      }
    }

    return () => {
      if (qrCodeRef.current) {
        qrCodeRef.current = null;
      }
    };
  }, [qrCodeData]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(checkoutUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy URL:", error);
    }
  };

  const handleOpenCheckout = () => {
    window.open(checkoutUrl, "_blank");
  };

  return (
    <div className="flex flex-col items-center gap-4 p-3 max-w-md">
      {/* Header */}
      <div className="e-center flex-col space-y-1 text-center">
        <h3 className="text-responsive-lg font-normal text-text-primary">
          {t("credits.payment.qrCode.title")}
        </h3>
        <p className="text-responsive-base font-normal text-text-secondary">
          {t("credits.payment.qrCode.description")}
        </p>
      </div>

      {/* QR Code */}
      <div className="flex flex-col items-center">
        <div ref={qrRef} className="e-center" />
        {creditTx?.purchaseMeta?.expiresAt && (
          <Countdown
            label="Mã hết hạn trong"
            deadline={creditTx.purchaseMeta.expiresAt}
            endLabel="Mã đã hết hạn"
          />
        )}
      </div>

      {/* Transaction Details */}
      {creditTx && creditTx.type === CreditTxTypeEnum.PURCHASE && (
        <div className="w-full bg-background-highlight p-3 rounded-sm">
          <h4 className="text-responsive-md font-normal text-text-primary mb-1">Transaction Details</h4>
          <div className="space-y-1 text-responsive-base font-normal">
            <div className="flex justify-between">
              <span className="text-text-secondary">Amount:</span>
              <span className="text-text-primary font-normal">{formatCredit(creditTx.amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Total amount:</span>
              <span className="text-text-primary font-normal">
                {formatCurrency(creditTx.amount * CREDIT_PRICE)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Payment Method:</span>
              <span className="text-text-primary font-normal capitalize">
                {creditTx.purchaseMeta?.method.replace("_", " ") || "Bank transfer"}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-secondary">Transaction ID:</span>
              <span className="text-text-primary font-normal">{creditTx.purchaseMeta?.txId || "N/A"}</span>
            </div>
          </div>
        </div>
      )}

      {/* Checkout URL */}
      <div className="w-full">
        <div className="flex items-stretch gap-2">
          <input
            type="text"
            value={checkoutUrl}
            readOnly
            className="flex-1 px-3 py-2 text-responsive-base border border-border-light rounded-sm bg-transparent text-text-primary focus:outline-none focus:border-border-dark focus:bg-background-highlight"
          />
          <Button variant="outline" onClick={handleCopyUrl} className="px-3 py-2">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 w-full">
        <Button variant="default" onClick={handleOpenCheckout} className="w-full">
          {t("credits.payment.qrCode.openCheckout")}
        </Button>
        <Button variant="outline" onClick={onClose} className="w-full">
          {t("common.close")}
        </Button>
      </div>
    </div>
  );
};
